import React from "react";
import "./EditUser.scss";
import * as api from "../../shared/api-client";
import * as _ from "lodash";
import { Button, Dialog, DialogContent, DialogContentText, TextField, DialogActions, Select, MenuItem, Chip, Checkbox, FormControlLabel, DialogTitleWithCloseButton, InlineInfoTooltip } from "../../shared/components";
import MetadataEditor from "./MetadataEditor";
import { Controller, useForm } from 'react-hook-form';
import { object, string, array } from 'yup';
import { yupResolver } from "@hookform/resolvers";
import { connect } from "react-redux";
import { showSuccess, showError } from "../../store/notifications/actions";

interface EditUserProps {
    displayTrigger: number,
    user?: api.UserDisplayModel,
    showSuccess: typeof showSuccess,
    showError: typeof showError,
    onSuccess: () => void
}

function EditUser(props: EditUserProps) {
    const schema = object().shape({
        name: string()
            .required("This field is required."),
        emailAddress: string()
            .required("This field is required."),
        roleIds: array(),
        metadataEntries: array()
    });
    const { displayTrigger, user, showSuccess, showError, onSuccess } = props;
    const { register, handleSubmit, errors, reset, setError, control } = useForm({ resolver: yupResolver(schema) });
    const [open, setOpen] = React.useState(false);
    const [rolesLookup, setRolesLookup] = React.useState([]);
    const [roleIds, setRoleIds] = React.useState([]);
    const [formErrors, setFormErrors] = React.useState(null);

    React.useEffect(() => {
        new api.LookupsClient().getRoles()
            .then(result => setRolesLookup(result));
    }, []);

    React.useEffect(() => {
        if (!displayTrigger) return;

        setRoleIds(user ? _.map(user.roles, r => r.id) : []);
        reset(user || {});
        setOpen(true);
    }, [displayTrigger]);

    const closeDialog = () => {
        setOpen(false);
    }

    const attachFormErrors = (errorDictionary: object) => {
        _.forOwn(errorDictionary, (value, key) => {
            if (key === "non_field_errors") {
                setFormErrors(value[0]);
            }
            else {
                setError(key, { type: "server", message: value[0] });
            }
        });
    }

    const save = (formModel) => {
        const model = {
            ...formModel,
            roleIds
        };

        // Bulk create...
        if (!user && (formModel.name.includes(",") || formModel.emailAddress.includes(","))) {
            let names = formModel.name.split(",").map(a => a.trim()) as string[];
            let emailAddresses = formModel.emailAddress.split(",").map(a => a.trim()) as string[];

            if (names.length !== emailAddresses.length) {
                setError("name", { type: "validation", message: "Length of names and email addresses do not match.  Please double-check both." });
                return;
            }

            let apiCalls = _.zip(names, emailAddresses).map(async ([name, emailAddress], index) => {
                // Add 1 second delay between API calls to bypass Auth0 rate limiting...
                await new Promise(res => setTimeout(res, 1000 * index));

                return await new api.UsersClient()
                    .create({
                        name,
                        emailAddress,
                        roleIds
                    })
                    .then(() => showSuccess(`${name} has been created successfully.`))
                    .catch((error: api.ApiException) => showError(`${name} was not created successfully - ${error.response}`));
            });

            Promise.all(apiCalls).then(() => {
                closeDialog();
                onSuccess();
            });

            return;
        }

        const apiCall = user ?
            new api.UsersClient().update(user.id, model) :
            new api.UsersClient().create(model);

        (apiCall as Promise<any>)
            .then(() => {
                closeDialog();
                showSuccess(`User has been ${user ? "updated" : "created"} successfully.`);
                onSuccess();
            })
            .catch((error: api.ApiException) => {
                if (error.status === 400) {
                    attachFormErrors(JSON.parse(error.response || "{}"));
                }
                else {
                    showError(error.response);
                }
            });
    };

    const getRole = (id) => {
        return _.find(rolesLookup, r => r.id === id);
    }

    return (

        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    closeDialog();
                }
            }}
            aria-labelledby="form-dialog-title">
            <DialogTitleWithCloseButton onClose={closeDialog} id="form-dialog-title">
                {`${user ? "Edit " + user.name : "Create User"}`}
            </DialogTitleWithCloseButton>
            <form onSubmit={handleSubmit(save)}>
                <DialogContent>
                    {
                        !user &&
                        <DialogContentText>
                            A welcome email will be sent to this user, containing instructions for login/registration.
                        </DialogContentText>
                    }
                    <br />
                    <TextField
                        name="name"
                        error={!!errors.name}
                        label="Name"
                        helperText={errors.name?.message}
                        inputRef={register}
                        variant="outlined"
                        fullWidth
                        autoFocus
                    />
                    <TextField
                        name="emailAddress"
                        error={!!errors.emailAddress}
                        label="Email Address"
                        helperText={errors.emailAddress?.message}
                        inputRef={register}
                        variant="outlined"
                        fullWidth
                    />
                    <Controller
                        name="enforceMFAUsage"
                        control={control}
                        render={p => (
                            <FormControlLabel
                                control={<Checkbox
                                    onChange={e => p.onChange(e.target.checked)}
                                    checked={p.value} />}
                                label="Enforce MFA Usage ?"
                            />
                        )}
                    />
                    <br />
                    <br />
                    <div className="select-header">Roles</div>
                    <Select
                        multiple
                        error={!!errors.roleIds}
                        value={roleIds}
                        onChange={(event) => setRoleIds(event.target.value as string[])}
                        variant="outlined"
                        fullWidth
                        inputProps={{
                            id: 'roles',
                        }}
                        renderValue={(selected) => (
                            <div>
                                {(selected as string[]).map((value) => (
                                    <Chip key={value} label={getRole(value)?.name} color="primary" className="multiselect-chip" />
                                ))}
                            </div>
                        )}
                    >
                        {rolesLookup.map((role) => (
                            <MenuItem key={role.id} value={role.id}>
                                {role.name}
                            </MenuItem>
                        ))}
                    </Select>
                    <br />
                    <br />
                    <div className="select-header">Metadata
                        <InlineInfoTooltip
                            text="Any additional information about the user can be captured here.  This will be available to the application and can be utilized for custom logic/bindings in storylines."
                        />
                    </div>
                    <Controller
                        name="metadataEntries"
                        control={control}
                        render={p => (
                            <MetadataEditor
                                updateEntries={p.onChange}
                                entries={p.value}
                            />
                        )}
                    />
                    {
                        formErrors &&
                        <div className="form-errors">
                            {formErrors}
                        </div>
                    }
                </DialogContent>
                <DialogActions>
                    <Button type="submit" variant="contained" color="primary">
                        {`${user?.id ? "Update" : "Create"} User`}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default connect(
    null,
    { showSuccess, showError })(EditUser);